import * as React from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import {
	getNextgenSettingsRequestTypesUrl,
	getNextgenSettingsUrl,
	getPortalGroupSettingsUrl,
	getTeamManagedPortalGroupSettingsUrl,
} from '@atlassian/help-center-common-util/url/url';

import { HELP_CENTERS_MANAGEMENT_BASE_PATH } from '@helpcenter/constants';
import { getHelpCenterAri } from '@helpcenter/meta';
import { di } from 'react-magnetic-di';
import { useRouter } from 'react-resource-router';

import type { State } from 'state';
import { fetchPortalAction } from 'state/actions/portal';
import {
	getCanAdministerProject,
	getIsProjectSimplified,
	getProjectKey,
} from 'state/selectors/portal';
import { getCanAdministerJIRA } from 'state/selectors/user';

import { useShouldShowPremiumUpsell } from '@atlassian/company-hub-common/use-should-show-premium-upsell';
import { HubPageType } from '@atlassian/company-hub-common/utils/hub-page-utils';

import { parseHelpCenterAri } from '../../../utils/ari-parser';
import portalButtonMessages from '../../common/customize-portal-buttons/messages';
import customizeMenuMessages from '../../layout/customise-menu-dumb/messages';
import { HubCustomizeMenu } from '../hub-customize-menu';
import type { HubCustomizeMenuSection } from '../hub-customize-menu';
import hubMessages from '../messages';
import { useHelpCenterCustomizeSection } from '../use-help-center-customize-section';
import { useIsTopicPage } from '../use-is-topic-page';

/**
 * Which page the menu is being opened from, as `HubPageType`. `PORTAL` and `CUSTOM`
 * offer an item that edits that page; every other hub page — profile, request
 * create, articles, approvals and so on — offers Edit help center, which opens the
 * hub home page in the editor. The hub home page itself has its own wrapper, so it
 * does not go through here.
 *
 * Passed in at the route rather than derived from the pageId, because these routes
 * carry the portal or page id as separate route params and never assemble the
 * prefixed pageId that `getHubPageType` reads.
 */
interface HubCustomizeNavActionsProps {
	iconColor?: string;
	pageType?: HubPageType;
}

interface ConnectedProps extends HubCustomizeNavActionsProps {
	routePortalId?: string;
}

interface StateProps {
	canAdministerJIRA: boolean;
	isProjectAdmin?: boolean;
	isProjectSimplified?: boolean;
	projectKey?: ReturnType<typeof getProjectKey>;
}

interface DispatchProps {
	fetchPortal: typeof fetchPortalAction;
}

const HubCustomizeNavActionsComponent = ({
	iconColor,
	pageType,
	canAdministerJIRA,
	fetchPortal,
	isProjectAdmin,
	isProjectSimplified,
	projectKey,
}: ConnectedProps & StateProps & DispatchProps) => {
	di(getHelpCenterAri, useRouter, useShouldShowPremiumUpsell, useIsTopicPage);
	const { formatMessage } = useIntl();
	const [{ match }] = useRouter();
	const helpCenterSection = useHelpCenterCustomizeSection(canAdministerJIRA);
	const portalSettingsEnabled = FeatureGates.checkGate('hubs-show-portal-settings');
	const shouldUpsellPremium = useShouldShowPremiumUpsell();

	const { portalId, id } = match.params;

	// Topic pages share the custom page route, so the item is only labelled for
	// the page it actually opens once the page's type is known.
	const isTopicPage = useIsTopicPage(pageType === HubPageType.CUSTOM ? id : undefined);

	React.useEffect(() => {
		if (
			!canAdministerJIRA ||
			pageType !== HubPageType.PORTAL ||
			!portalId ||
			(typeof isProjectAdmin === 'boolean' &&
				typeof isProjectSimplified === 'boolean' &&
				typeof projectKey === 'string')
		) {
			return;
		}

		const portalIdAsNumber = Number(portalId);
		if (Number.isSafeInteger(portalIdAsNumber)) {
			fetchPortal({ id: portalIdAsNumber });
		}
	}, [
		canAdministerJIRA,
		fetchPortal,
		isProjectSimplified,
		isProjectAdmin,
		pageType,
		portalId,
		projectKey,
	]);

	const editSection = React.useMemo<HubCustomizeMenuSection | null>(() => {
		if (!canAdministerJIRA) {
			return null;
		}

		// A page-specific route missing its own id falls back to Edit help center
		// rather than linking to whichever page the stale param belonged to.
		let label = formatMessage(hubMessages.editHelpCenterLabel);
		let suffix = '';
		if (pageType === HubPageType.PORTAL && portalId) {
			// Same editor either way, but Free and Standard sites can only change the
			// announcement on a portal page, so the item promises only that.
			label = formatMessage(
				shouldUpsellPremium
					? hubMessages.editPortalAnnouncementLabel
					: hubMessages.editPortalPageLabel,
			);
			suffix = `/portal/${portalId}`;
		} else if (pageType === HubPageType.CUSTOM && id) {
			label = formatMessage(
				isTopicPage ? hubMessages.editTopicPageLabel : hubMessages.editCustomPageLabel,
			);
			suffix = `/${id}`;
		}

		const { helpCenterId } = parseHelpCenterAri(getHelpCenterAri());
		const normalizedProjectKey =
			typeof projectKey === 'string' ? projectKey.toUpperCase() : undefined;

		const isPortalAdmin =
			pageType === HubPageType.PORTAL &&
			isProjectAdmin &&
			normalizedProjectKey &&
			typeof isProjectSimplified === 'boolean';

		return {
			items: [
				{
					label,
					testId: 'edit-hub-link',
					href: `${HELP_CENTERS_MANAGEMENT_BASE_PATH}/${helpCenterId}/edit-hub${suffix}`,
					// Same id this item carried in the avatar menu, so its click
					// analytics survive the move.
					actionSubjectId: 'navbarCustomiseHub',
				},
				...(isPortalAdmin && portalSettingsEnabled
					? [
							{
								label: formatMessage(customizeMenuMessages.configurePortal),
								testId: 'configure-portal-link',
								href: `${getNextgenSettingsUrl(normalizedProjectKey)}/${isProjectSimplified ? 'channels/portal-settings' : 'portal-settings'}`,
								actionSubjectId: 'navbarConfigurePortal',
								target: '_blank' as const,
							},
							{
								label: formatMessage(customizeMenuMessages.customizeRequestTypes),
								testId: 'customize-request-types-link',
								href: isProjectSimplified
									? getNextgenSettingsRequestTypesUrl(normalizedProjectKey)
									: `${getNextgenSettingsUrl(normalizedProjectKey)}/request-types/`,
								actionSubjectId: 'navbarCustomizeRequestTypes',
								target: '_blank' as const,
							},
						]
					: []),
				...(isPortalAdmin
					? [
							{
								label: formatMessage(portalButtonMessages.managePortalGroupsLabel),
								testId: 'manage-portal-groups-link',
								href: isProjectSimplified
									? getTeamManagedPortalGroupSettingsUrl(normalizedProjectKey)
									: getPortalGroupSettingsUrl(normalizedProjectKey),
								actionSubjectId: 'managePortalGroupsButton',
							},
						]
					: []),
			],
		};
	}, [
		canAdministerJIRA,
		formatMessage,
		id,
		isTopicPage,
		portalSettingsEnabled,
		pageType,
		portalId,
		isProjectSimplified,
		isProjectAdmin,
		projectKey,
		shouldUpsellPremium,
	]);

	const sections = React.useMemo(
		() =>
			[editSection, helpCenterSection].filter((section): section is HubCustomizeMenuSection =>
				Boolean(section),
			),
		[editSection, helpCenterSection],
	);

	return <HubCustomizeMenu sections={sections} iconColor={iconColor} />;
};

const ConnectedHubCustomizeNavActions = connect(
	(state: State, { routePortalId }: ConnectedProps): StateProps => {
		const portalIdAsNumber = routePortalId !== undefined ? Number(routePortalId) : undefined;

		return {
			canAdministerJIRA: getCanAdministerJIRA(state),
			isProjectAdmin: getCanAdministerProject(state, portalIdAsNumber),
			isProjectSimplified: getIsProjectSimplified(state, portalIdAsNumber),
			projectKey: getProjectKey(state, portalIdAsNumber),
		};
	},
	{ fetchPortal: fetchPortalAction },
)(HubCustomizeNavActionsComponent);

export const HubCustomizeNavActions = (props: HubCustomizeNavActionsProps) => {
	di(useRouter);
	const [{ match }] = useRouter();
	const routePortalId =
		typeof match.params.portalId === 'string' ? match.params.portalId : undefined;

	return <ConnectedHubCustomizeNavActions {...props} routePortalId={routePortalId} />;
};
